import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { RoleService } from '../services/role.service';
import { LoggerService } from '../services/logger.service';
import { UserRole } from '../shared/types/role.interface';

/**
* Route guard that restricts access to admin routes to users with the SUPER_ADMIN role by validating a super-admin token stored separately in localStorage and synchronizing the RoleService.
* @component
* @selector app-super-admin-guard
* @example
*   <app-super-admin-guard [inputProp]="sample_value1" (outputEvent)="handler()"></app-super-admin-guard>
* @input N/A none - This guard has no Angular @Input properties; it is an injectable service used by the Router.
* @output N/A none - This guard does not emit Angular @Output events; canActivate returns boolean | Promise<boolean> | Observable<boolean>.
*
* Purpose:
*   - Ensures only users with UserRole.SUPER_ADMIN can activate protected admin routes.
*   - Reads and validates a separate 'superAdminData' entry from localStorage (protecting against invalid JSON).
*   - Initializes the RoleService with the super-admin role when appropriate.
*   - Redirects non-authorized users to the appropriate dashboard or to the admin login page.
*
* Key methods:
*   - canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Promise<boolean> | Observable<boolean>
*       * Validates presence and JSON integrity of 'superAdminData' and its token.
*       * Sets RoleService role to SUPER_ADMIN when indicated.
*       * Checks current user role from RoleService; allows navigation only for SUPER_ADMIN.
*       * Logs decisions and redirects to login or role-appropriate default route as needed.
*   - private redirectToLogin(returnUrl: string): void
*       * Logs and navigates to '/admin/login' with a returnUrl query parameter.
*
* Important notes / lifecycle:
*   - Provided in root via @Injectable({ providedIn: 'root' }) so it is a singleton service.
*   - No Angular lifecycle hooks are used (e.g., ngOnInit) since this is a router guard service.
*   - Relies on injected RoleService, Router, and LoggerService for role resolution, navigation, and logging.
*/
@Injectable({
  providedIn: 'root'
})
export class SuperAdminGuard implements CanActivate {
  private roleService = inject(RoleService);
  private router = inject(Router);
  private logger = inject(LoggerService);

  /**
  * Guard that permits route activation only for authenticated users with the SUPER_ADMIN role by validating stored superAdminData token and role, and redirecting otherwise.
  * @example
  * anActivate(routeSnapshot, routerStateSnapshot)
  * true
  * @param {ActivatedRouteSnapshot} route - The activated route snapshot to check.
  * @param {RouterStateSnapshot} state - The router state snapshot containing the target URL.
  * @returns {Observable<boolean>|Promise<boolean>|boolean} True if access is granted to a super admin, otherwise false (or an observable/promise resolving to false).
  **/
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    // Check if super admin data exists in local storage (separate from regular userData)
    let superAdminData = null;
    if (typeof window !== 'undefined') {
      const superAdminDataStr = localStorage.getItem('superAdminData');
      if (superAdminDataStr) {
        try {
          superAdminData = JSON.parse(superAdminDataStr);
        } catch (error) {
          this.logger.error('Invalid JSON in superAdminData', { error }, 'SuperAdminGuard');
          localStorage.removeItem('superAdminData');
        }
      }
    }
    
    if (!superAdminData || !superAdminData.token) {
      this.logger.info('Access denied - No valid super admin token', { route: state.url }, 'SuperAdminGuard');
      this.redirectToLogin(state.url);
      return false;
    }

    // Initialize role service with super admin role from superAdminData
    // This ensures role is set correctly even if regular userData exists
    if (superAdminData.role === UserRole.SUPER_ADMIN) {
      this.roleService.setUserRole(UserRole.SUPER_ADMIN);
    }

    // Get user role from the role service
    const userRole = this.roleService.getCurrentUserRole();
    
    if (!userRole) {
      this.logger.warn('Access denied - No user role found', { route: state.url }, 'SuperAdminGuard');
      this.redirectToLogin(state.url);
      return false;
    }

    // Only allow SUPER_ADMIN to access admin routes
    if (userRole !== UserRole.SUPER_ADMIN) {
      this.logger.warn('Access denied - Not a super admin', { 
        originalUrl: state.url,
        userRole 
      }, 'SuperAdminGuard');
      
      // Redirect to appropriate dashboard based on role
      const defaultRoute = this.roleService.getDefaultRoute(userRole);
      this.router.navigate([defaultRoute]);
      return false;
    }

    this.logger.debug('Access granted - Super admin', { 
      originalUrl: state.url,
      userRole 
    }, 'SuperAdminGuard');
    
    return true;
  }

  private redirectToLogin(returnUrl: string): void {
    this.logger.info('Redirecting to admin login', { returnUrl }, 'SuperAdminGuard');
    this.router.navigate(['/admin/login'], { 
      queryParams: { returnUrl } 
    });
  }
}
